import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import classes from "./Profile.module.css";
import API from "../../utils/api";
import RenderToast from "../../components/RenderToast";
import ChangePasswordModal from "../../Modal/ChangePassword";
import CreateUserModal from "../../Modal/User/CreateUserModal";
import LoaderComp from "../../components/LoaderComp";

const roles = [
  { value: "admin", label: "Admin" },
  { value: "user", label: "User" },
];

const emptyProfile = {
  name: "",
  email: "",
  phone: "",
  role: "admin",
  photo: "",
};

const readUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export default function Profile() {
  const navigate = useNavigate();
  const [storedUser, setStoredUser] = useState(() => readUser());
  const [profile, setProfile] = useState(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [downloadingDb, setDownloadingDb] = useState(false);

  const userId = storedUser?.id;
  const isAdmin = storedUser?.role === "admin";

  useEffect(() => {
    const onStorage = () => setStoredUser(readUser());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (!userId) {
      navigate("/tech-login-478");
      return;
    }

    const fetchProfile = async () => {
      try {
        const formData = new FormData();
        formData.append("action", "fetch");
        formData.append("user_id", userId);
        const token = localStorage.getItem("authToken") || "";

        const response = await fetch(`${API.base}/profile.php`, {
          method: "POST",
          headers: { Authorization: token },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || "Failed to load profile");
        }
        setProfile({
          name: data.user.name || "",
          email: data.user.email || "",
          phone: data.user.phone || "",
          role: data.user.role || "admin",
          photo: data.user.photo || "",
        });
        localStorage.setItem("user", JSON.stringify(data.user));
        setStoredUser(data.user);
      } catch (err) {
        RenderToast({ type: "error", message: err.message });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate, userId]);

  useEffect(() => {
    if (!photoFile) {
      setPhotoPreview("");
      return;
    }
    const objectUrl = URL.createObjectURL(photoFile);
    setPhotoPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [photoFile]);

  const previewPhoto = useMemo(() => {
    if (photoPreview) return photoPreview;
    if (profile.photo) {
      const baseUrl = API.base.endsWith("/techmatter_api")
        ? API.base
        : `${API.base}/techmatter_api`;
      return `${baseUrl}/upload/${profile.photo}`;
    }
    return "/placeholder-image.jpg";
  }, [photoPreview, profile.photo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setPhotoFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!profile.name || !profile.email) {
      return RenderToast({
        type: "error",
        message: "Name and email are required",
      });
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("authToken") || "";
      const formData = new FormData();
      formData.append("action", "update");
      formData.append("user_id", userId);
      formData.append("name", profile.name);
      formData.append("email", profile.email);
      formData.append("phone", profile.phone || "");
      // Only admin can change role - and only for their own profile if they're admin
      if (isAdmin && profile.role) {
        formData.append("role", profile.role);
      }
      if (photoFile) formData.append("photo", photoFile);

      const response = await fetch(`${API.base}/profile.php`, {
        method: "POST",
        headers: { Authorization: token },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to update profile");
      }
      RenderToast({ type: "success", message: "Profile updated successfully" });
      localStorage.setItem("user", JSON.stringify(data.user));
      setStoredUser(data.user);
      setProfile((prev) => ({
        ...prev,
        name: data.user.name,
        email: data.user.email,
        phone: data.user.phone,
        role: data.user.role,
        photo: data.user.photo,
      }));
      setPhotoFile(null);
      setPhotoPreview("");
      // Trigger user update event to refresh all components
      window.dispatchEvent(new Event("userUpdated"));
    } catch (err) {
      RenderToast({ type: "error", message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadDatabase = async () => {
    if (!isAdmin) return;

    setDownloadingDb(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API.base}/backup_database.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({ action: "download" }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to download database");
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to download database");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `techmatteruk_backup_${new Date()
        .toISOString()
        .replace(/[-:]/g, "")
        .slice(0, 15)}.sql`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      RenderToast({
        type: "success",
        message: "Database backup downloaded successfully",
      });
    } catch (err) {
      RenderToast({
        type: "error",
        message: err.message || "Failed to download database backup",
      });
    } finally {
      setDownloadingDb(false);
    }
  };

  return (
    <DashboardLayout>
      {loading ? (
        <LoaderComp />
      ) : (
        <div className={classes.wrapper}>
          {/* Top Header */}
          <div className={classes.headerBar}>
            <div>
              <p className={classes.kicker}>Account</p>
              <h1>Profile Settings</h1>
              <p className={classes.subtitle}>
                Update your personal information and password
              </p>
            </div>
            <div className={classes.headerActions}>
              {isAdmin && (
                <button
                  className={classes.addUserBtn}
                  onClick={() => setShowCreateUserModal(true)}
                >
                  Add User
                </button>
              )}
              <button
                className={classes.actionBtn}
                onClick={() => setShowPasswordModal(true)}
              >
                Change Password
              </button>
            </div>
          </div>

          {/* Profile Card */}
          <form className={classes.formCard} onSubmit={handleSubmit}>
            <div className={classes.avatarRow}>
              <div style={{ position: "relative" }}>
                <div className={classes.avatarPreview}>
                  <img src={previewPhoto} alt="Profile avatar" />
                </div>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={handleDownloadDatabase}
                    className={classes.downloadDbBtn}
                    title="Download Database Backup"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </button>
                )}
              </div>
              <div>
                <p className={classes.avatarLabel}>Profile Photo</p>
                <p className={classes.avatarHint}>
                  Recommended size 300x300px. JPG, PNG, WEBP.
                </p>
                <label className={classes.uploadBtn}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                  Upload new photo
                </label>
              </div>
            </div>

            <div className={classes.grid}>
              <label>
                <span>Full Name</span>
                <input
                  type="text"
                  name="name"
                  value={profile.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                />
              </label>
              <label>
                <span>Email Address</span>
                <input
                  type="email"
                  name="email"
                  value={profile.email}
                  onChange={handleChange}
                  placeholder="email@example.com"
                />
              </label>
              <label>
                <span>Phone</span>
                <input
                  type="tel"
                  name="phone"
                  value={profile.phone}
                  onChange={handleChange}
                  placeholder="+44 1234 567890"
                />
              </label>
              {isAdmin ? (
                <label>
                  <span>Role</span>
                  <select
                    name="role"
                    value={profile.role}
                    onChange={handleChange}
                  >
                    {roles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <label>
                  <span>Role</span>
                  <input type="text" value={profile.role} disabled readOnly />
                </label>
              )}
            </div>

            <div className={classes.actions}>
              <div className={classes.hint}>
                Need help? Contact the site administrator to change critical
                data.
              </div>
              <button
                type="submit"
                className={classes.primaryBtn}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ChangePasswordModal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        userId={userId}
      />
      <CreateUserModal
        open={showCreateUserModal}
        onClose={() => setShowCreateUserModal(false)}
        onCreated={() => {}}
      />
    </DashboardLayout>
  );
}
